export const DAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

/** Monday–Sunday labels for weekday pickers (Open Booking, Activities, etc.). */
export const WEEKDAY_LABELS_MON_SUN = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const;

type WeekdayLabel = (typeof WEEKDAY_LABELS_MON_SUN)[number];

const WEEKDAY_LABELS_LOWER = new Map(
  WEEKDAY_LABELS_MON_SUN.map((d) => [d.toLowerCase(), d] as const),
);

/** Legacy / abbreviated tokens (e.g. M, T, W, TH, F) → canonical label. */
const WEEKDAY_TOKEN_ALIASES: Record<string, WeekdayLabel> = {
  m: "Monday",
  mon: "Monday",
  mo: "Monday",
  monday: "Monday",
  t: "Tuesday",
  tu: "Tuesday",
  tue: "Tuesday",
  tues: "Tuesday",
  tuesday: "Tuesday",
  w: "Wednesday",
  wed: "Wednesday",
  weds: "Wednesday",
  wednesday: "Wednesday",
  th: "Thursday",
  thu: "Thursday",
  thur: "Thursday",
  thurs: "Thursday",
  r: "Thursday",
  thursday: "Thursday",
  f: "Friday",
  fri: "Friday",
  friday: "Friday",
  sa: "Saturday",
  sat: "Saturday",
  saturday: "Saturday",
  su: "Sunday",
  sun: "Sunday",
  sunday: "Sunday",
};

const resolveWeekdayToken = (token: string): WeekdayLabel | undefined => {
  const t = token.trim();
  if (!t) {
    return undefined;
  }
  const lower = t.toLowerCase();
  const fromLabel = WEEKDAY_LABELS_LOWER.get(lower);
  if (fromLabel) {
    return fromLabel;
  }
  return WEEKDAY_TOKEN_ALIASES[lower];
};

export const parseDaysOfWeekStored = (raw: string): WeekdayLabel[] => {
  const s = raw.trim();
  if (!s) {
    return [];
  }
  if (/^all\s+days\b/i.test(s) || /all\s+days\s+of\s+the\s+week/i.test(s)) {
    return [...WEEKDAY_LABELS_MON_SUN];
  }
  const out: WeekdayLabel[] = [];
  for (const p of s.split(",")) {
    const canon = resolveWeekdayToken(p);
    if (canon && !out.includes(canon)) {
      out.push(canon);
    }
  }
  return out;
};

export const formatDaysOfWeekPayload = (days: readonly string[]): string => {
  if (days.length === 0) {
    return "";
  }
  const set = new Set(days);
  const allSelected = WEEKDAY_LABELS_MON_SUN.every((d) => set.has(d));
  if (allSelected) {
    return "All Days of the week";
  }
  return [...WEEKDAY_LABELS_MON_SUN].filter((d) => set.has(d)).join(", ");
};

/** Always comma-separated Monday–Sunday labels (no "All Days" shorthand). */
export const formatDaysOfWeekCommaSeparated = (
  days: readonly string[],
): string => {
  if (days.length === 0) {
    return "";
  }
  const set = new Set<WeekdayLabel>();
  for (const d of days) {
    const canon = resolveWeekdayToken(String(d));
    if (canon) {
      set.add(canon);
    }
  }
  return [...WEEKDAY_LABELS_MON_SUN].filter((d) => set.has(d)).join(", ");
};

const DAYJS_DAY_TO_WEEKDAY: WeekdayLabel[] = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

/** Parse YYYY-MM-DD (or Date) as local midnight to avoid UTC day-shift. */
const toLocalDateOnly = (
  value: string | Date,
): Date | null => {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return null;
    }
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const match = String(value)
    .trim()
    .match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const year = Number(match[1]);
    const month = Number(match[2]) - 1;
    const day = Number(match[3]);
    const local = new Date(year, month, day);
    if (
      local.getFullYear() !== year ||
      local.getMonth() !== month ||
      local.getDate() !== day
    ) {
      return null;
    }
    return local;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

/**
 * True when every selected weekday appears at least once in the inclusive
 * start–end date range (e.g. 2026-10-07..2026-10-09 only covers Wed–Fri).
 */
export const dateRangeCoversSelectedWeekdays = (
  startDate: string | Date | null | undefined,
  endDate: string | Date | null | undefined,
  selectedDays: readonly string[],
): boolean => {
  if (!startDate || !endDate || selectedDays.length === 0) {
    return false;
  }

  const start = toLocalDateOnly(startDate);
  const end = toLocalDateOnly(endDate);
  if (!start || !end) {
    return false;
  }
  if (end < start) {
    return false;
  }

  const selected = new Set<WeekdayLabel>();
  for (const d of selectedDays) {
    const canon = resolveWeekdayToken(String(d));
    if (canon) {
      selected.add(canon);
    }
  }
  if (selected.size === 0) {
    return false;
  }

  const covered = new Set<WeekdayLabel>();
  const cursor = new Date(start);
  while (cursor <= end) {
    covered.add(DAYJS_DAY_TO_WEEKDAY[cursor.getDay()]);
    cursor.setDate(cursor.getDate() + 1);
  }

  return Array.from(selected).every((day) => covered.has(day));
};
